<template>
  <nav class="breadcrumbs" :aria-label="t('accessibility.breadcrumb')">
    <ol class="breadcrumbs__list">
      <li
        v-for="(item, index) in filteredItems"
        :key="index"
        class="breadcrumbs__item"
        :class="{ 'breadcrumbs__item--ancestor': !isCurrent(index) }"
      >
        <span v-if="isCurrent(index)" class="breadcrumbs__label breadcrumbs__current" aria-current="page">
          {{ item.label }}
        </span>
        <router-link v-else-if="item.to" :to="item.to" class="breadcrumbs__label breadcrumbs__link">
          {{ item.label }}
        </router-link>
        <span v-else class="breadcrumbs__label breadcrumbs__ancestor">
          {{ item.label }}
        </span>
        <span v-if="!isCurrent(index)" class="breadcrumbs__separator" aria-hidden="true">
          /
        </span>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

export interface BreadcrumbItem {
  label: string
  to?: string | { name: string; params?: Record<string, string> }
}

interface Props {
  items: BreadcrumbItem[]
}

const props = defineProps<Props>()

const filteredItems = computed(() =>
  props.items.filter(item => item.label?.trim())
)

const isCurrent = (index: number) => index === filteredItems.value.length - 1
</script>

<style scoped>
/*
 * The trail lives in a 64px bar, so it is one line at every width: each label
 * ellipsizes inside its own box rather than spilling into its neighbour, and the
 * earlier levels give up their width long before the current page does.
 */
.breadcrumbs,
.breadcrumbs__list,
.breadcrumbs__item {
  min-width: 0;
}

.breadcrumbs__list {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs);
  list-style: none;
  margin: 0;
  padding: 0;
}

.breadcrumbs__item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.breadcrumbs__item--ancestor {
  flex: 0 1000 auto;
}

.breadcrumbs__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.breadcrumbs__link,
.breadcrumbs__ancestor {
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
}

.breadcrumbs__link {
  text-decoration: none;
  transition: color var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.breadcrumbs__link:hover {
  color: var(--color-brand-primary);
  text-decoration: underline;
}

.breadcrumbs__current {
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

.breadcrumbs__separator {
  flex-shrink: 0;
  color: var(--color-text-placeholder);
  font-size: var(--font-size-md);
  user-select: none;
}

@media (--breakpoint-lg-down) {
  .breadcrumbs__current {
    font-size: var(--font-size-lg);
  }
}

/* A phone has room for the page title and nothing before it. */
@media (--breakpoint-md-down) {
  .breadcrumbs__item--ancestor {
    display: none;
  }

  .breadcrumbs__current {
    font-size: var(--font-size-md);
  }
}
</style>
